"use client";

import { LensCustomizationData, TranslationData } from "@/common/lib/types";
import TextTruncate from "@/components/common/textTruncate/TextTruncate";
import LazyImage from "@/components/lazyImage/LazyImage";
import RenderPrice from "@/components/productPrice/RenderPrice";
import Button from "@/components/ui/button/Button";
import { cn } from "@/lib/tailwind";
import { getLangFromLocale } from "@/utils/common";
import { translate } from "@/utils/lensConfigurator";
import Image from "next/image";
import { useMemo, useState } from "react";
import RecommendedBadge from "../../RecommendedBadge";
import LensTypeOverlay from "./LensTypeOverlay";
import { getPromotionBadgeNameFromPromotions } from "@/utils/promotionUtils";
import RenderCurrencyInText from "@/components/common/RenderCurrencyInText";

interface LensOptionProps {
  locale: string;
  className: string;
  currencyCode: string;
  onSelect: () => void;
  item: LensCustomizationData;
  translations: TranslationData;
}

// TODO(QA): When Qatar (qa) is enabled, replace this hardcode with NEXT_PUBLIC_PERCENTAGE_SYMBOL_HIDE_COUNTRIES
// via getPercentHideCountries()/displayPercentage (same as Magrabi LCP).
let displayPercentage = true;

const LensCustomizeCard = ({
  item,
  locale,
  onSelect,
  className,
  translations,
  currencyCode,
}: LensOptionProps) => {
  const lang = getLangFromLocale(locale);
  const [openLensTypeOverlay, setOpenLensTypeOverlay] = useState(false);
  const promoBadgeName = useMemo(
    () => getPromotionBadgeNameFromPromotions(item.availablePromotions, lang),
    [item.availablePromotions, lang],
  );

  return (
    <div className={cn("ccol-span-full flex h-full flex-col xl:flex-row bg-tertiary-3 xl:min-h-60 xl:relative", className)}>
      {item.badge && <RecommendedBadge className="xl:absolute xl:left-4 xl:top-4 w-full text-center xl:w-[98px]" text={item.badge} />}
      <div className="xl:w-[248px] flex gap-4 px-4 pt-4 xl:px-0 xl:pt-0">
        <LazyImage
          width={248}
          height={240}
          src={item.media}
          alt={item?.title[lang] + " banner"}
          className="h-[59px] w-20 xl:w-full xl:h-full object-cover"
        />
        <div className="sm:px-0 xl:px-3 xl:hidden">
          <h3 className="text-sm font-semibold">{translate(item.title, lang)}</h3>
          {item.subtitle && <p className="text-[0.75rem] text-tertiary-6">{item.subtitle}</p>}
        </div>
      </div>
      <div className="flex flex-1 flex-col px-3 py-4">
        <div className="xl:flex xl:flex-col gap-[1px] hidden">
          <h3 className="text-sm font-semibold">{translate(item.title, lang)}</h3>
          {item.subtitle && <p className="text-[0.75rem] text-tertiary-6">{item.subtitle}</p>}
        </div>
        <hr className="-mx-3 my-[10px] h-[2px] bg-tertiary-9" />
        <ul className="mb-3 grid grid-cols-2 gap-[6px] ">
          {item.features?.map((feature, index) => (
            <li key={index} className="flex items-center gap-2 text-sm">
              <Image src={feature.icon} alt={feature.title} width={12} height={12} className="" />
              {feature.title}
            </li>
          ))}
        </ul>
        <TextTruncate
          text={item.description}
          translations={translations}
          className="mb-2 text-left text-[0.813rem] sm:text-[0.813rem]"
          truncateProps={{ className: "text-[0.813rem] sm:text-[0.813rem]", offset: 140 }}
        />
        <hr className="-mx-3 my-[10px] h-[2px] bg-tertiary-9" />
        <div className="flex items-center justify-end gap-[15px] ">
          <div className="flex gap-1 text-[0.813rem] font-bold">
            {item?.offerPrice ? (
              <div className="text-sm font-normal">
                {item?.price && (
                  <p className="line-through">
                    <RenderPrice price={item?.price} currencyCode={currencyCode} lang={lang} />
                  </p>
                )}
                {item?.offerPrice && (
                  <p className="font-semibold text-secondary-1">
                    + <RenderPrice price={item?.offerPrice} currencyCode={currencyCode} lang={lang} />
                  </p>
                )}
                {item?.offerPercent && displayPercentage && <p>{item.offerPercent}</p>}
                {promoBadgeName ? (
                  <p className="mt-1 text-sm font-semibold text-secondary-1">
                    <RenderCurrencyInText text={promoBadgeName} />
                  </p>
                ) : null}
              </div>
            ) : (
              <p className="text-sm">
                {item.custom ? "From" : ""}{" "}
                <RenderPrice price={item?.price} currencyCode={currencyCode} lang={lang} />
              </p>
            )}
          </div>
          <Button className="px-4 py-0 sm:px-4 sm:py-0 md:px-4 md:py-0" onClick={onSelect}>
            {translations?.lensConfigurator?.lens_config_select_button_label}
          </Button>
        </div>
      </div>
      <LensTypeOverlay open={openLensTypeOverlay} onClose={() => setOpenLensTypeOverlay(false)} />
    </div>
  );
};

export default LensCustomizeCard;
